<script setup lang="ts">
  import { getCurrentInstance, computed, ref } from 'vue';
  import { usePrintingSettingsModal } from '@/composition/printing/use-printing-settings-modal';
  import {
    deviceCanEverPrint,
    usePrinting,
    usePrintingStore,
  } from '@/composition/use-printing';
  import { useNativeJobActions } from '@/composition/printing/use-native-job-actions';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { IPrintJob } from '@/interfaces/printing.interfaces';
  import { PrintPluginStatus } from '@/modules/PrintService';

  const ui = useUi();
  const modals = useModal();
  const printHelper = usePrinting();
  const printingStore = usePrintingStore();
  const printingSettingsModal = usePrintingSettingsModal();
  const jobActions = useNativeJobActions();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const isOpen = ref(false);

  /**
   * Same gated flow as the Jobs tab (shared composable): confirm dialog for
   * possibly-printed jobs, resubmission retargeted to the destination's
   * CURRENT assignment, and a NEW job record either way.
   */
  async function reprint(printJob: IPrintJob) {
    if (!jobActions.canReprint(printJob)) return;
    if (jobActions.busyJobIds.value.includes(printJob.jobId)) return;

    if (jobActions.needsReprintConfirm(printJob)) {
      const accepted = await modals.confirm({
        title: i18nT('printingV2.reprintConfirmTitle'),
        message: i18nT('printingV2.reprintConfirmBody'),
        acceptText: i18nT('printingV2.reprint'),
      });
      if (!accepted) return;
    }

    try {
      await jobActions.resubmit(printJob);
      ui.showToast(AlertType.Success, i18nT('printingV2.reprintSent'));
    } catch (e) {
      console.error('[PrintingDropdown] reprint failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.actionFailed'));
    }
  }

  const printingUnavailable = computed(() => {
    return (
      printHelper.status.value === PrintPluginStatus.Disconnected ||
      printHelper.isOutdatedVersion.value
    );
  });

  /** Passive nudge indicator; the interstitial opens via settings entries. */
  const upgradeAvailable = printHelper.upgradeAvailable;

  const btnColor = computed(() => {
    if (printingUnavailable.value) {
      return 'error';
    }

    if (printHelper.status.value === PrintPluginStatus.Connecting) {
      return 'warning';
    }

    if (printingStore.lastPrintJobs.length) {
      return 'info';
    }

    return 'grey';
  });

  function openPrintingSettings() {
    printingSettingsModal.open();
  }
</script>

<template>
  <!-- Hidden entirely on platforms that can never print (mobile/tablet browsers) -->
  <div v-if="deviceCanEverPrint" class="printing-dropdown">
    <!-- Visual-only (pointer-events none): it overlaps the fab's corner and
         must not steal its clicks. -->
    <span v-if="upgradeAvailable" class="upgrade-indicator" />
    <v-menu
      v-model="isOpen"
      bottom
      :close-on-content-click="false"
      offset-y
      :disabled="printingUnavailable"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="printing-dropdown__btn"
          fab
          small
          depressed
          :color="btnColor"
          dark
          v-bind="!printingUnavailable ? attrs : undefined"
          v-on="!printingUnavailable ? on : undefined"
          @click="printingUnavailable && openPrintingSettings()"
        >
          <v-icon>mdi-printer</v-icon>
        </v-btn>
      </template>
      <div>
        <v-list class="print-list">
          <template v-if="!printingStore.lastPrintJobs.length">
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title
                  >No hay impresiones recientes</v-list-item-title
                >
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-else>
            <div class="list-title-container">
              <span class="list-title">Ultimas impresiones</span>
              <v-btn
                x-small
                color="primary"
                depressed
                rounded
                @click="openPrintingSettings"
              >
                <v-icon left>mdi-cog</v-icon>
                Configuracion
              </v-btn>
            </div>
            <v-list-item
              v-for="printJob of printingStore.lastPrintJobs"
              :key="printJob.jobId"
              class="print-item"
            >
              <v-list-item-content>
                <v-list-item-title>
                  <v-icon
                    class="pr-1"
                    small
                    :color="
                      printJob.status === 'failed'
                        ? 'error'
                        : printJob.status === 'unknown'
                        ? 'warning'
                        : undefined
                    "
                  >
                    {{
                      printJob.status === 'printed'
                        ? 'mdi-printer'
                        : 'mdi-printer-alert'
                    }}
                  </v-icon>
                  {{ printJob.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span
                    v-if="printJob.status === 'failed'"
                    class="error--text tw-text-xs"
                  >
                    Falló:
                    {{ printJob.errorMessage || 'error desconocido' }}
                  </span>
                  <!-- Post-send status report: the printer took the data and
                       then flagged a problem — the ticket MAY have printed. -->
                  <span
                    v-else-if="printJob.status === 'unknown'"
                    class="warning--text tw-text-xs"
                  >
                    Verificar:
                    {{ printJob.errorMessage || 'estado desconocido' }}
                  </span>
                  <timeago
                    v-if="isOpen"
                    class="timeago"
                    :datetime="printJob.createdAt"
                    :auto-update="60"
                  ></timeago>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="printJob.data && printJob.data.length">
                <v-btn
                  small
                  color="info"
                  depressed
                  rounded
                  :loading="jobActions.busyJobIds.value.includes(printJob.jobId)"
                  @click="reprint(printJob)"
                >
                  <v-icon left>mdi-printer</v-icon>
                  Reimprimir
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </div>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
  .printing-dropdown {
    @apply tw-relative;

    .new-indicator {
      @apply tw-absolute tw-w-full tw-h-full tw-rounded-full tw-left-0 tw-bottom-0 tw-right-0 tw-top-0 tw-animate-ping;
      z-index: 5;
    }

    .upgrade-indicator {
      @apply tw-pointer-events-none tw-absolute tw-right-0 tw-top-0 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-amber-400;
      z-index: 7;
    }

    .printing-dropdown__btn {
      z-index: 6;
    }
  }

  .print-list {
    @apply tw-divide-y tw-divide-dashed;
    max-height: 80vh;
    max-height: 80dvh;

    .list-title-container {
      @apply tw-flex tw-justify-between tw-items-center tw-space-x-2 tw-px-4;

      .list-title {
        @apply tw-text-sm tw-font-medium tw-text-neutral-500;
      }
    }
  }
</style>
